import { apiClient } from '../lib/apiClient'

export interface LegalDocument {
  id: string
  document_type: 'terms' | 'privacy' | 'sla' | 'aup' | 'dpa'
  version: string
  title: string
  content: string
  summary: string
  effective_date: string
  is_published: boolean
  requires_acceptance: boolean
  created_at: string
  updated_at: string
}

export interface LegalAcceptance {
  id: string
  user_id: string
  org_id: string | null
  document_type: string
  document_version: string
  accepted_at: string
  ip_address: string | null
  user_agent: string | null
  acceptance_method: 'signup' | 'update' | 'reactivation' | 'manual'
  created_at: string
  display_name?: string
  user_email?: string
}

export interface AcceptanceStatus {
  document_type: string
  has_accepted: boolean
  latest_version: string
  accepted_version: string | null
}

export const legalService = {
  async getLatestPublishedDocuments(types?: string[]): Promise<LegalDocument[]> {
    const params = new URLSearchParams()
    if (types && types.length > 0) {
      params.set('types', types.join(','))
    }
    const query = params.toString() ? `?${params.toString()}` : ''
    return apiClient.get<LegalDocument[]>(`/legal/documents${query}`)
  },

  async getRequiredDocuments(): Promise<LegalDocument[]> {
    return apiClient.get<LegalDocument[]>('/legal/documents/required')
  },

  async captureClientInfo(): Promise<{ ip_address: string | null; user_agent: string }> {
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
      const response = await fetch(`${apiBase}/capture-ip`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        throw new Error('Failed to capture client info')
      }

      const data = await response.json()
      return {
        ip_address: data.ip_address === 'unknown' ? null : data.ip_address,
        user_agent: data.user_agent || navigator.userAgent,
      }
    } catch (error) {
      console.warn('Could not fetch IP address:', error)
      return {
        ip_address: null,
        user_agent: navigator.userAgent,
      }
    }
  },

  async recordAcceptance(
    userId: string,
    orgId: string | null,
    documentType: string,
    documentVersion: string,
    acceptanceMethod: 'signup' | 'update' | 'reactivation' | 'manual' = 'manual'
  ): Promise<void> {
    const clientInfo = await this.captureClientInfo()

    await apiClient.post('/legal/acceptances', {
      user_id: userId,
      org_id: orgId,
      document_type: documentType,
      document_version: documentVersion,
      ip_address: clientInfo.ip_address,
      user_agent: clientInfo.user_agent,
      acceptance_method: acceptanceMethod,
    })
  },

  async recordMultipleAcceptances(
    userId: string,
    orgId: string | null,
    documents: Array<{ type: string; version: string }>,
    acceptanceMethod: 'signup' | 'update' | 'reactivation' | 'manual' = 'signup'
  ): Promise<void> {
    for (const doc of documents) {
      await this.recordAcceptance(userId, orgId, doc.type, doc.version, acceptanceMethod)
    }
  },

  async getUserAcceptances(userId: string): Promise<LegalAcceptance[]> {
    return apiClient.get<LegalAcceptance[]>(`/users/${userId}/legal-acceptances`)
  },

  async checkUserAcceptanceStatus(userId: string): Promise<AcceptanceStatus[]> {
    return apiClient.get<AcceptanceStatus[]>(`/users/${userId}/legal-acceptances/status`)
  },

  async hasUserAcceptedAllRequired(userId: string): Promise<boolean> {
    const status = await this.checkUserAcceptanceStatus(userId)
    return status.every(s => s.has_accepted)
  },

  async getOrgMemberAcceptances(orgId: string): Promise<LegalAcceptance[]> {
    return apiClient.get<LegalAcceptance[]>(`/orgs/${orgId}/legal-acceptances`)
  },
}
